import { logger } from "@/utils/loggingUtils";

/**
 * Recovery for stale lazy-route chunk imports (EUBU-2758).
 *
 * After a deploy the hashed chunk files change. A client that loaded the app
 * before the deploy can navigate to a lazy route and request a chunk that no
 * longer exists. Retrying the same URL never succeeds (iOS Safari / Chrome
 * iOS additionally cache the failed module resolution), so the only reliable
 * recovery is a one-time full page reload to pick up the fresh index.html
 * and the new chunk hashes.
 *
 * The reload is guarded by a sessionStorage timestamp so a genuinely broken
 * deploy cannot put the browser into an infinite reload loop.
 */

const RELOAD_GUARD_KEY = "euka-learning-chunk-reload-ts";

/** Minimum time between automatic chunk-recovery reloads. */
export const RELOAD_GUARD_WINDOW_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  // iOS Safari / Chrome iOS wording
  /importing a module script failed/i,
  // Chrome / Edge wording
  /failed to fetch dynamically imported module/i,
  // Firefox wording
  /error loading dynamically imported module/i,
  // Webpack-style grouping kept for safety
  /chunkloaderror/i,
  // Vite CSS/asset preload helper failure
  /unable to preload css/i,
  // React.lazy resolved a stale/missing module after a deploy
  /_result\.default/i,
  /reading 'default'/i
];

/** True when the error is a failed dynamic import of a lazy chunk. */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : String(error);
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

const readLastReloadTs = (): number => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    // sessionStorage unavailable (private mode edge cases) — treat as
    // "already reloaded" so we never risk a reload loop we cannot guard.
    return Date.now();
  }
};

/**
 * Perform a one-time full page reload to recover from a stale chunk.
 *
 * Returns `true` when a reload has been initiated (caller should keep the
 * user on the loading state), or `false` when a reload was already attempted
 * within the guard window (caller should surface its normal error UI).
 */
export const reloadOnceForStaleChunk = (context: Record<string, unknown> = {}): boolean => {
  const now = Date.now();
  if (now - readLastReloadTs() < RELOAD_GUARD_WINDOW_MS) {
    return false;
  }

  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // If we cannot persist the guard we must not reload, otherwise a
    // broken deploy would reload forever.
    return false;
  }

  logger.warn("Stale lazy chunk detected — reloading page to fetch new assets", context);
  window.location.reload();
  return true;
};
